import { ImageParsedValue, ImageValue, ParsedFields } from './withJSSContext'
import { VideoSource, VideoType } from './sharedTypes'
import React from 'react'
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react'

export const parseImageSource = (urlString: string | { src: string }, isEditing?: boolean): string | undefined => {
  if (!urlString) return
  const src = typeof urlString !== 'string' ? urlString?.src : urlString

  if ((isEditing && src.includes('default_image.svg')) || src.includes('jssmedia')) {
    return src
  }

  return src.replace('media', 'jssmedia')
}

export const formatImage = ({ src, alt, width, height }: ImageValue, isEditing: boolean): ImageValue | undefined => {
  const parsedSrc = parseImageSource(src, isEditing)
  if (parsedSrc) {
    return {
      src: parsedSrc,
      alt,
      width,
      height,
    }
  }
}

type VideoSourceFormatterAttrs = {
  videoType: VideoType
  brightcove: {
    videoUrl?: string
    videoId?: string
    accountId?: string
  }
  youtube: {
    videoUrl?: string
    videoId?: string
  }
  embedded: {
    videoUrl?: string
  }
}

export const videoSourceFormatter = ({
  videoType,
  brightcove,
  youtube,
  embedded,
}: VideoSourceFormatterAttrs): string | undefined => {
  const addHttps = ({ videoUrl, ...rest }: { videoUrl?: string }): VideoSource => {
    return {
      videoUrl: videoUrl && videoUrl?.startsWith('https') ? videoUrl : `https:${videoUrl}`,
      ...rest,
    }
  }

  const videoStructure: Record<VideoType, VideoSource> = {
    brightcove: addHttps(brightcove),
    youtube: addHttps(youtube),
    embedded: addHttps(embedded),
  }

  const { videoUrl, videoId, accountId }: VideoSource = videoType ? videoStructure[videoType] : {}

  const decoder: Record<string, string | undefined> = {
    '{accountId}': accountId,
    '{videoId}': videoId,
    '{YouTubeId}': videoId,
  }

  return videoUrl?.replace(/{accountId}|{videoId}|{YouTubeId}/gi, (match) => decoder[match] as string)
}

//TODO import isType from DSL repository
export const isType = <T>(item: unknown, property: keyof T, value?: T[typeof property]): item is T => {
  if (typeof value === 'undefined') {
    return (item as T)?.[property] != undefined
  }
  return (item as T)?.[property] === value
}

export const hasFieldValues = (parsedFields: ParsedFields): boolean => {
  return !!parsedFields && Object.keys(parsedFields).filter((key) => key !== 'instanceCount').length !== 0
}

export const hasSpecificFieldValues = (parsedFields: ParsedFields, keys: string[]): boolean => {
  if (parsedFields) {
    return keys.some((key) => Object.keys(parsedFields).includes(key))
  } else {
    return false
  }
}

type RenderFieldsType = {
  children: React.ReactElement
  parsedFields: ParsedFields
}

const BaseRenderFields: React.FC<RenderFieldsType & { sitecoreContext: { pageEditing: boolean } }> = ({
  children,
  parsedFields,
  sitecoreContext: { pageEditing },
}) => (hasFieldValues(parsedFields) || pageEditing ? children : null)

export const RenderFields = withSitecoreContext()(BaseRenderFields as React.FC) as React.FC<RenderFieldsType>

export const sanitizeUrl = (url: string): string =>
  url
    .replace(/[^\w-]+/g, '-')
    .replace(/--+/g, '-')
    .replace(/^-/, '')
    .replace(/-$/, '')

export const getAspectRatio = (image: ImageValue): ImageParsedValue | undefined => {
  if (!image) return
  const { src, alt, width, height } = image
  let aspectRatio
  if (width && height) {
    aspectRatio = Number(width) / Number(height)
  }

  return { src, alt, aspectRatio: aspectRatio }
}
